import React, { useState } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { Sparkles, Loader2, RefreshCw, BookOpen, Plus } from 'lucide-react';
import ScoreMeters from '../ScoreMeters';
import EvaluationTagChips from '../EvaluationTagChips';
import { extractTagEvaluation, hasLegacyNumericScores } from '../../utils/evaluationDisplay';
import ModalWrapper, { ModalCard } from '../ModalWrapper';
import { useTranslation } from '../../hooks/useTranslation.jsx';
import AIButton from '../AIButton';

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const HeaderIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: rgba(168, 85, 247, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #A855F7;
`;

const HeaderTitle = styled.h2`
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: #fff;
`;

const HeaderSubtitle = styled.div`
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.4);
  margin-top: 2px;
`;


const Body = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px 24px 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const LoadingState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 48px 0;
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.9rem;
`;

const Spinner = styled(Loader2)`
  animation: ${spin} 1s linear infinite;
  color: #A855F7;
`;

const ErrorState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 32px 0;
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.85rem;
  text-align: center;
`;

const SuggestionCard = styled.label`
  display: flex;
  gap: 12px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid ${props => props.$checked
    ? 'rgba(168, 85, 247, 0.4)'
    : 'rgba(255, 255, 255, 0.08)'};
  border-radius: 12px;
  padding: 16px;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: rgba(168, 85, 247, 0.3);
    background: rgba(255, 255, 255, 0.05);
  }
`;

const Checkbox = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 6px;
  border: 2px solid ${props => props.$checked
    ? '#A855F7'
    : 'rgba(255, 255, 255, 0.2)'};
  background: ${props => props.$checked
    ? '#A855F7'
    : 'transparent'};
  flex-shrink: 0;
  margin-top: 1px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;

  &::after {
    content: '${props => props.$checked ? '\\2713' : ''}';
    color: #fff;
    font-size: 12px;
    font-weight: 700;
  }
`;

const CardContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const CardTitle = styled.div`
  font-size: 0.95rem;
  font-weight: 600;
  color: #fff;
  line-height: 1.3;
  margin-bottom: 6px;
`;

const CardDescription = styled.div`
  font-size: 0.82rem;
  color: rgba(255, 255, 255, 0.55);
  line-height: 1.5;
  margin-bottom: 8px;
`;

const CardMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const TypeBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 0.7rem;
  font-weight: 500;
  background: ${props => props.$type === 'obstacle'
    ? 'rgba(233, 75, 110, 0.12)'
    : 'rgba(166, 214, 77, 0.12)'};
  color: ${props => props.$type === 'obstacle' ? '#E94B6E' : '#A6D64D'};
`;

const NoSourcesBadge = styled.span`
  font-size: 0.65rem;
  color: rgba(255, 255, 255, 0.3);
  font-style: italic;
`;

const RefTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.65rem;
  background: rgba(255, 255, 255, 0.05);
  color: rgba(255, 255, 255, 0.4);
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
`;

const RegenerateBtn = styled(AIButton)`
  border-radius: 8px;
  font-size: 0.82rem;
  font-weight: 500;
  padding: 8px 16px;
`;

const AddSelectedBtn = styled(AIButton)`
  padding: 8px 18px;
  border-radius: 8px;
  font-size: 0.85rem;
`;
AddSelectedBtn.defaultProps = { $variant: 'solid' };

/**
 * AILensItemsModal — Shows AI-generated obstacle/opportunity suggestions.
 * User selects with checkboxes and clicks "Lisaa valitut" to add them all.
 */
export default function AILensItemsModal({
  type,
  lensName,
  pivotPointTitle,
  suggestions = [],
  loading = false,
  error = null,
  onAccept,
  onRegenerate,
  onClose,
  showDescription = true,
}) {
  const { t } = useTranslation();
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [adding, setAdding] = useState(false);
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleAddSelected = async () => {
    const selected = suggestions.filter(s => selectedIds.has(s.id));
    if (selected.length === 0) return;
    setAdding(true);
    for (const s of selected) {
      await onAccept?.(s);
    }
    onClose?.();
  };

  const typeLabel = type === 'obstacle' ? t('lenses.obstacles') : t('lenses.opportunities');
  const selectedCount = selectedIds.size;

  return (
    <ModalWrapper visible onClose={onClose} zIndex={1000} opacity={0.7}>
      <ModalCard size="sm" style={{ border: '1px solid rgba(168, 85, 247, 0.3)', animation: `${slideUp} 0.2s ease` }}>
        <Header>
          <HeaderLeft>
            <HeaderIcon>
              <Sparkles size={16} />
            </HeaderIcon>
            <div>
              <HeaderTitle>{t('lenses.aiSuggestionsTitle', { type: typeLabel })}</HeaderTitle>
              <HeaderSubtitle>{lensName} &middot; {pivotPointTitle}</HeaderSubtitle>
            </div>
          </HeaderLeft>
        </Header>

        <Body>
          {loading && (
            <LoadingState>
              <Spinner size={28} />
              {t('lenses.aiAnalyzingResearch')}
              <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.3)', marginTop: 4 }}>
                {t('lenses.aiAnalyzingHint')}
              </div>
            </LoadingState>
          )}

          {error && !loading && (
            <ErrorState>
              {t('lenses.aiSuggestFailed')}
              <div style={{ color: '#E94B6E', fontSize: '0.78rem' }}>{error}</div>
            </ErrorState>
          )}

          {!loading && !error && suggestions.length === 0 && (
            <ErrorState>
              {t('lenses.noSuggestions')}
            </ErrorState>
          )}

          {!loading && suggestions.map(s => {
            const checked = selectedIds.has(s.id);
            const tagEval = extractTagEvaluation(s.scores);
            const legacyScores = !tagEval && hasLegacyNumericScores(s.scores) ? s.scores : null;
            return (
              <SuggestionCard key={s.id} $checked={checked} onClick={() => toggleSelect(s.id)}>
                <Checkbox $checked={checked} />
                <CardContent>
                  <CardTitle>{s.title}</CardTitle>
                  {showDescription && s.description && (
                    <CardDescription>{s.description}</CardDescription>
                  )}
                  {(s.references || []).length > 0 && (
                    <CardMeta>
                      {s.references.map((ref, i) => (
                        <RefTag key={i}>
                          <BookOpen size={9} />
                          {ref}
                        </RefTag>
                      ))}
                    </CardMeta>
                  )}
                  {tagEval && (
                    <div style={{ marginTop: 4 }}>
                      <EvaluationTagChips
                        tags={tagEval.tags}
                        contradictsResearch={tagEval.contradicts_research}
                        researchStance={tagEval.research_stance}
                        compact
                        noTopMargin
                      />
                    </div>
                  )}
                  {legacyScores && (
                    <div style={{ marginTop: 4 }}>
                      <ScoreMeters scores={legacyScores} size="medium" expandable />
                    </div>
                  )}
                </CardContent>
              </SuggestionCard>
            );
          })}
        </Body>

        {!loading && suggestions.length > 0 && (
          <Footer>
            <RegenerateBtn onClick={onRegenerate} disabled={loading || adding}>
              <RefreshCw size={14} />
              {t('lenses.suggestMore')}
            </RegenerateBtn>
            <AddSelectedBtn onClick={handleAddSelected} disabled={selectedCount === 0 || adding}>
              <Plus size={15} />
              {adding ? t('lenses.adding') : t('lenses.addSelected', { count: selectedCount })}
            </AddSelectedBtn>
          </Footer>
        )}
      </ModalCard>
    </ModalWrapper>
  );
}
